import React, { useState, useEffect, useRef, CSSProperties } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import CheckWithCircle from "../../svg/CheckWithCircle";

type Props = {
  // `isDirty` is optional and defaults to undefined, so the ~6 existing call
  // sites that pass only {label, key} render exactly as before. When set, an
  // amber dot follows the label — used by the Profile tabs to mark which tab
  // holds unsaved changes.
  // `isLogged` is likewise optional and additive: the nutrition plan's
  // meal-type tabs set it to show a green check on meals the customer has
  // logged. Tabs that don't pass it render exactly as before.
  tabs: { label: string; key: number; isDirty?: boolean; isLogged?: boolean }[];
  selectedTab: number;
  setSelectedTab: Function;
  isFullWidth?: boolean;
  tabStyle?: CSSProperties;
  // Let the tabs flow onto further lines instead of overflowing. The
  // underline then follows the active tab's own line (it is placed by top
  // rather than pinned to the strip's bottom). Opt-in: the existing single-line
  // pickers keep their layout exactly.
  wrap?: boolean;
};

const SecondaryTabMenuPicker = ({
  tabs,
  selectedTab,
  setSelectedTab,
  isFullWidth = true,
  tabStyle,
  wrap = false,
}: Props) => {
  const theme = useTheme();
  const menuRef = useRef<HTMLDivElement>(null);
  const [underlinePosition, setUnderlinePosition] = useState(0);
  const [underlineWidth, setUnderlineWidth] = useState(0);
  const [underlineTop, setUnderlineTop] = useState(0);

  // Adjust underline position and width when the location changes
  useEffect(() => {
    setTimeout(() => {
      if (selectedTab !== -1 && menuRef.current) {
        const menuItems = menuRef.current.querySelectorAll(
          `[data-testid^="menu-tab-"]`
        );
        const activeItem = menuItems[selectedTab] as HTMLElement;

        if (activeItem) {
          setUnderlinePosition(activeItem.offsetLeft);
          setUnderlineWidth(activeItem.offsetWidth);
          setUnderlineTop(activeItem.offsetTop + activeItem.offsetHeight - 2);
        }
      }
    }, 20);
  }, [selectedTab, tabs]);

  return (
    <div style={styles.container} ref={menuRef}>
      <div
        style={{
          ...styles.menuTabs,
          width: isFullWidth ? "100%" : "auto",
          // Wrapped lines keep the 24px column gap but no row gap: each tab's
          // own 8px vertical padding already spaces the lines.
          ...(wrap && {
            flexWrap: "wrap" as "wrap",
            justifyContent: "flex-start",
            rowGap: 0,
          }),
        }}
      >
        {tabs.map((tab, index) => (
          <div
            key={index}
            data-testid={`menu-tab-${index}`}
            style={{
              ...styles.menuTab(theme, selectedTab === index),
              ...tabStyle,
            }}
            onClick={() => setSelectedTab(index)}
          >
            {tab.label}
            {/* Reserved, not conditional: a tab that opts into the marker keeps
                the dot's width at all times and only toggles its visibility, so
                becoming dirty never re-flows the strip (and the animated
                underline never has to chase a changing tab width). */}
            {tab.isDirty !== undefined && (
              <span style={styles.dirtyDot(theme, tab.isDirty)} />
            )}
            {tab.isLogged && (
              <span style={styles.loggedCheck}>
                <CheckWithCircle />
              </span>
            )}
          </div>
        ))}
        {/* Dynamic underline based on active NavLink position and width */}
        <div
          style={{
            ...styles.menuUnderline(theme, underlinePosition, underlineWidth),
            ...(wrap && { bottom: "auto", top: `${underlineTop}px` }),
          }}
        />
      </div>
    </div>
  );
};

export default SecondaryTabMenuPicker;

const styles = {
  container: {
    width: "100%",
    display: "flex",
  },
  menuTabs: {
    display: "flex",
    flexDirection: "row" as "row",
    justifyContent: "space-between",
    position: "relative" as "relative",
    gap: "24px",
  },
  menuTab: (theme: any, isActive: boolean) => ({
    padding: "8px 0",
    cursor: "pointer",
    fontSize: "14px",
    fontFamily: "var(--font-Medium)",
    transition: "color 0.3s",
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: isActive
      ? theme.colors_ds.NEUTRAL_TEXT_STRONG
      : theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  // Unsaved-changes marker; mirrors the dot on the sticky save bar.
  dirtyDot: (theme: any, isDirty: boolean) => ({
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: theme.colorTokens.W[60],
    visibility: (isDirty ? "visible" : "hidden") as "visible" | "hidden",
  }),
  // Logged-meal marker for the nutrition plan tabs.
  loggedCheck: {
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
  },
  menuUnderline: (
    theme: any,
    underlinePosition: number,
    underlineWidth: number
  ) => ({
    position: "absolute" as "absolute",
    bottom: 0,
    left: 0,
    height: "2px",
    backgroundColor: theme.colors_ds.PRIMARY_ICON_MAIN,
    transform: `translateX(${underlinePosition}px)`,
    width: `${underlineWidth}px`,
    transition: "transform 0.4s ease, width 0.4s ease",
  }),
};
